import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { officeAPI, ordersAPI } from '../api';
import { setHandOver, setMonthLoaded, setOrders } from './orderSlice';
import { CLIENT_NOTS, getClientNotify } from './notifySlice';
import { addCategory, deleteCategory, editCategory } from './settingSlice';


const initialState = {
    isLoaded: false,
    error: null,
    segmentError: {
        orders: null,
        revenues: null,
        expenses: null,
    },
    expenses: [],
    revenues: [],
    balances: [],
    months: [],
};

export const getOfficeData = createAsyncThunk(
    'office/getData',
    async ({ month }, thunkAPI) => {

        const {loaded: isOrdersLoaded, timestamp} = thunkAPI.getState().orders.months.find(
            (m) => m.month === month,
        );

        const isOfficeLoaded = thunkAPI.getState().office.months.find(
            (m) => m.month === month,
        ).loaded;

        if (!isOrdersLoaded || !isOfficeLoaded) {
            thunkAPI.dispatch(setLoaded(false));
        }

        if (!isOrdersLoaded) {
            const loadedOrders = thunkAPI.getState().orders.list.filter(o => o.details.monthStamp === timestamp).map(o => o.id);
            const ordersData = await ordersAPI.get(month, loadedOrders);
            if (ordersData.isOkay) {
                thunkAPI.dispatch(setMonthLoaded({month}));
                thunkAPI.dispatch(setOrders(ordersData.orders));
            } else {
                thunkAPI.dispatch(setSegmentError({orders: ordersData.message}));
            }
        }
        if (!isOfficeLoaded) {
            const revenuesData = await officeAPI.getRevenues(month);
            if (revenuesData.isOkay) {
                thunkAPI.dispatch(setRevenues(revenuesData.revenues));
            } else {
                thunkAPI.dispatch(setSegmentError({revenues: revenuesData.message}));
            }
            const expensesData = await officeAPI.getExpenses(month);
            if (expensesData.isOkay) {
                thunkAPI.dispatch(setExpenses(expensesData.expenses));
            } else {
                thunkAPI.dispatch(setSegmentError({expenses: expensesData.message}));
            }
            const balancesData = await officeAPI.getBalances(month);
            if (balancesData.isOkay) {
                thunkAPI.dispatch(setBalances(balancesData.balances));
            }
        }
        thunkAPI.dispatch(changeOfficeMonth(month));
        thunkAPI.dispatch(setOfficeMonthLoaded({}));
        thunkAPI.dispatch(setLoaded(true));

    },
);

export const createOfficeExpense = createAsyncThunk(
    'office/createExpense',
    async (expenseData, thunkAPI) => {
        const data = await officeAPI.createExpense(expenseData);
        thunkAPI.dispatch(getClientNotify({data, action: CLIENT_NOTS.CREATE_EXPENSE}));
        if (data.isOkay) {
            thunkAPI.dispatch(setExpenses([data.expense]));
            thunkAPI.dispatch(updateBalances(data.balances));
        } else {
            console.log(data);
        }
    },
);

export const createOfficeRevenue = createAsyncThunk(
    'office/createRevenue',
    async (revenueData, thunkAPI) => {
        const data = await officeAPI.createRevenue(revenueData);
        thunkAPI.dispatch(getClientNotify({data, action: CLIENT_NOTS.CREATE_REVENUE}));
        if (data.isOkay) {
            thunkAPI.dispatch(setRevenues([data.revenue]));
            thunkAPI.dispatch(updateBalances(data.balances));
        } else {
            console.log(data);
        }
    },
);
export const deleteOfficeRevenue = createAsyncThunk(
    'office/deleteRevenue',
    async (revenueId, thunkAPI) => {
        const data = await officeAPI.deleteRevenue(revenueId);
        thunkAPI.dispatch(getClientNotify({data, action: CLIENT_NOTS.DELETE_REVENUE}));
        if (data.isOkay) {
            thunkAPI.dispatch(deleteRevenue(data.revenueId));
            thunkAPI.dispatch(updateBalances(data.balances));
        } else {
            console.log(data);
        }
    },
);

export const editOfficeExpense = createAsyncThunk(
    'office/createExpense',
    async (expenseData, thunkAPI) => {
        const data = await officeAPI.editExpense(expenseData);
        thunkAPI.dispatch(getClientNotify({data, action: CLIENT_NOTS.EDIT_EXPENSE}));
        if (data.isOkay) {
            thunkAPI.dispatch(updateExpense(data.expense));
            data.balances && thunkAPI.dispatch(updateBalances(data.balances));
        } else {
            console.log(data);
        }
    },
);

export const deleteOfficeExpense = createAsyncThunk(
    'office/deleteExpense',
    async (expenseId, thunkAPI) => {
        const data = await officeAPI.deleteExpense(expenseId);
        thunkAPI.dispatch(getClientNotify({data, action: CLIENT_NOTS.DELETE_EXPENSE}));
        if (data.isOkay) {
            thunkAPI.dispatch(dropExpense(data.expenseId));
            thunkAPI.dispatch(updateBalances(data.balances))
        } else {
            console.log(data);
        }
    },
);

export const handOver = createAsyncThunk(
    'office/handOver',
    async ({orderIds, resetFunc, enableButton}, thunkAPI) => {
        const data = await officeAPI.handOver(orderIds);
        enableButton();
        const successAnswers = data.list.filter((d) => d.isOkay);
        if (successAnswers.length) {
            thunkAPI.dispatch(getClientNotify({data: successAnswers[0], action: CLIENT_NOTS.HAND_OVER}));
            successAnswers.forEach((ans) => {
                thunkAPI.dispatch(setHandOver({orderId: ans.orderId, value: true}));
                thunkAPI.dispatch(setRevenues([ans.revenue]));
            })
            data.balances && thunkAPI.dispatch(updateBalances(data.balances));
            resetFunc();
        }
        const hasErrorAnswer = data.list.find((d) => !d.isOkay);
        if (hasErrorAnswer) {
            thunkAPI.dispatch(getClientNotify({data: hasErrorAnswer, action: CLIENT_NOTS.HAND_OVER}));
        }
    },
);

export const returnFromOffice = createAsyncThunk(
    'office/returnFromOffice',
    async ({revenueIds, resetFunc, enableButton}, thunkAPI) => {
        const data = await officeAPI.returnOrder(revenueIds);
        enableButton();
        const successAnswers = data.list.filter((d) => d.isOkay);
        if (successAnswers.length) {
            thunkAPI.dispatch(getClientNotify({data: successAnswers[0], action: CLIENT_NOTS.RETURN_FROM_OFFICE}));
            successAnswers.forEach((ans) => {
                thunkAPI.dispatch(setHandOver({orderId: ans.orderId, value: false}));
                thunkAPI.dispatch(deleteRevenue(ans.revenueId));
            })
            data.balances && thunkAPI.dispatch(updateBalances(data.balances));
            resetFunc();
        }
        const hasErrorAnswer = data.list.find((d) => !d.isOkay);
        if (hasErrorAnswer) {
            thunkAPI.dispatch(getClientNotify({data: hasErrorAnswer, action: CLIENT_NOTS.RETURN_FROM_OFFICE}));
        }
    },
);

export const createOfficeCategory = createAsyncThunk('office/createCategory', async (categoryData, thunkAPI) => {
    const data = await officeAPI.createCategory(categoryData);
    thunkAPI.dispatch(getClientNotify({data, action: CLIENT_NOTS.CREATE_CATEGORY}));
    if (data.isOkay) {
        thunkAPI.dispatch(addCategory(data.category));
    } else {
        console.log(data);
    }
});
export const editOfficeCategory = createAsyncThunk('office/editCategory', async (categoryData, thunkAPI) => {
    const data = await officeAPI.editCategory(categoryData);
    thunkAPI.dispatch(getClientNotify({data, action: CLIENT_NOTS.EDIT_CATEGORY}));
    if (data.isOkay) {
        thunkAPI.dispatch(editCategory(data.category));
        thunkAPI.dispatch(updateExpensesCategory(data.category));
    } else {
        console.log(data);
    }
});
export const deleteOfficeCategory = createAsyncThunk('office/deleteCategory', async (categoryId, thunkAPI) => {
    const data = await officeAPI.deleteCategory(categoryId);
    thunkAPI.dispatch(getClientNotify({data, action: CLIENT_NOTS.DELETE_CATEGORY}));
    if (data.isOkay) {
        thunkAPI.dispatch(deleteCategory(data.categoryId));
        data.expenses && thunkAPI.dispatch(updateExpenses(data.expenses));
        // thunkAPI.dispatch(updateUser(data.worker));
    } else {
        console.log(data);
    }
});

export const createPromoterExpense = createAsyncThunk('office/createPromoterExpense', async (finishId, thunkAPI) => {
    // const data = await officeAPI.createPromoterExpense(finishId);
    // thunkAPI.dispatch(getClientNotify({data, action: CLIENT_NOTS.CREATE_EXPENSE}));
    // if (data.isOkay) {
    //     thunkAPI.dispatch(setExpenses([data.expense]));
    //     thunkAPI.dispatch(updateBalances(data.balances));
    // } else {
    //     console.log(data);
    // }
});

const officeSlice = createSlice({
    name: 'office',
    initialState,
    reducers: {
        clearOfficeState(state) {
            state.error = null;
            state.isLoaded = false;
            state.segmentError = {
                orders: null,
                revenues: null,
                expenses: null,
            };
            state.expenses = [];
            state.revenues = [];
            state.balances = [];
            state.months = [];
        },
        setOfficeMonths(state, { payload }) {
            state.months = payload;
        },
        setError(state, {payload}) {
            state.error = payload;
        },
        updateBalance(state, {payload}) {
            state.balances = state.balances.map(b => (b.city === payload.city && b.timestamp === payload.timestamp) ? payload : b)
        },
        setSegmentError(state, {payload}) {
            state.segmentError = {
                orders: payload.orders || state.segmentError.orders,
                revenues: payload.revenues || state.segmentError.revenues,
                expenses: payload.expenses || state.segmentError.expenses,
            }
        },
        setLoaded(state, { payload }) {
            state.isLoaded = payload;
        },
        setOfficeMonthLoaded(state, _) {
            state.months = state.months.map(m => m.active ? {
                ...m,
                loaded: true,
            } : m);
        },
        changeOfficeMonth(state, {payload}) {
            state.months = state.months.map(m => m.month === payload ? ({...m, active: true}) : ({...m, active: false}))
        },
        setRevenues(state, {payload}) {
            state.revenues = [...state.revenues, ...payload];
        },
        deleteRevenue(state, {payload}) {
            state.revenues = state.revenues.filter((r) => r._id !== payload);
        },
        setExpenses(state, {payload}) {
            state.expenses = [...state.expenses, ...payload];
        },
        updateExpense(state, {payload}) {
            state.expenses = state.expenses.map(e => e._id === payload._id ? payload : e);
        },
        updateExpenses(state, {payload}) {
            state.expenses = state.expenses.map(e => {
                const newExp = payload.find((e2) => e2._id === e._id);
                return newExp || e;
            })
        },
        dropExpense(state, {payload}) {
            state.expenses = state.expenses.filter(e => e._id !== payload);
        },
        dropExpenses(state, {payload}) {
            state.expenses = state.expenses.filter((exp) => {
                if (payload.some((e) => e === exp._id)) {
                    return false;
                }
                return true;
            })
        },
        setBalances(state, {payload}) {
            state.balances = [...state.balances, ...payload];
        },
        updateExpensesCategory(state, {payload}) {
            state.expenses = state.expenses.map(e => {
                if (e.category._id !== payload._id) return e;
                return {
                    ...e,
                    category: {
                        _id: payload._id,
                        name: payload.name,
                        color: payload.details.color,
                        undeletable: payload.details.undeletable
                    }
                }
            })
        },
        updateBalances(state, {payload}) {
            state.balances = payload;
        },
    },
    extraReducers: (builder) => {},
});

export const { clearOfficeState, setOfficeMonths, updateExpense, dropExpenses, updateExpensesCategory, dropExpense, deleteRevenue, updateBalance, updateExpenses, updateBalances, setLoaded, setRevenues, setExpenses, setBalances, setOfficeMonthLoaded, changeOfficeMonth, setError, setSegmentError } = officeSlice.actions;

export default officeSlice.reducer;
